'use client';

import { useTheme } from 'next-themes';
import type { ToasterProps } from 'sonner';
import { Toaster as Sonner } from 'sonner';

//MFE Shadow Dom Fix, uncomment next lines to apply
import { useShadowRoot } from '@access-evo/react-app-api';
import { useEffect, useRef } from 'react';

export function addStylesToShadowDOM(shadowRoot: ShadowRoot, selector: string): () => void {
  const isStyleAlreadyInShadowDOM = (styleContent: string | null): boolean => {
    if (!styleContent) return false;

    const existingStyles = Array.from(shadowRoot.querySelectorAll('style'));
    return existingStyles.some((existing) => existing.textContent === styleContent);
  };

  const addStyleToShadowDOM = (styleElement: HTMLStyleElement): void => {
    if (!isStyleAlreadyInShadowDOM(styleElement.textContent)) {
      const clonedStyle = styleElement.cloneNode(true) as HTMLStyleElement;
      shadowRoot.appendChild(clonedStyle);
    }
  };

  const processStyleElement = (element: HTMLStyleElement): void => {
    if (element.textContent?.includes(selector)) {
      addStyleToShadowDOM(element);
    }
  };

  const addMatchingStyles = () => {
    const styleElements = document.head.querySelectorAll('style');
    for (const styleEl of styleElements) {
      processStyleElement(styleEl);
    }
  };

  const handleAddedNode = (node: Node): void => {
    const element = node as Element;
    if (element.nodeType === Node.ELEMENT_NODE && element.tagName === 'STYLE') {
      processStyleElement(element as HTMLStyleElement);
    }
  };

  const handleMutations = (mutations: MutationRecord[]): void => {
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        for (const node of mutation.addedNodes) {
          handleAddedNode(node);
        }
      }
    }
  };

  // Add styles immediately if available
  addMatchingStyles();

  // Set up a MutationObserver to catch dynamically added styles
  const observer = new MutationObserver(handleMutations);
  observer.observe(document.head, {
    childList: true,
    subtree: true,
  });

  // Return cleanup function
  return () => {
    observer.disconnect();
  };
}

function useShadowStyles(shadowRoot: ShadowRoot | null, selector: string): void {
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!shadowRoot) {
      return;
    }

    // Clean up previous observer if it exists
    if (cleanupRef.current) {
      cleanupRef.current();
    }

    // Set up style watching
    cleanupRef.current = addStylesToShadowDOM(shadowRoot, selector);

    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [shadowRoot, selector]);
}

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = 'system' } = useTheme();

  // MFE Shadow Dom Fix, uncomment next lines to apply
  const shadowRoot = useShadowRoot();
  useShadowStyles(shadowRoot, '[data-sonner-toaster]');

  return (
    <Sonner
      theme={theme as ToasterProps['theme']}
      className="toaster group"
      style={
        {
          '--normal-bg': 'var(--popover)',
          '--normal-text': 'var(--popover-foreground)',
          '--normal-border': 'var(--border)',
        } as React.CSSProperties
      }
      {...props}
    />
  );
};

export { Toaster };
